import { format } from 'date-fns'
import head from 'lodash-es/head'
import isArray from 'lodash-es/isArray'
import omit from 'lodash-es/omit'
import * as Yup from 'yup'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import { ROUTE_UI_SECTION_NAME } from '../../../constants/route'
import { SERVER_NON_FIELD_ERROR_KEY } from '../../../constants/validation'
import i18n from '../../../i18n'
import { getItemsIds } from '../../../utils/common'
import { getCleanValues } from '../../../utils/formHelpers'
import { getFlatProfilePathname } from '../../../utils/link'
import {
  parseServerKey,
  parseServerValidation,
} from '../../../utils/validation'

import {
  FLAT_FORM_FIELD_NAME_BUILDING,
  FLAT_FORM_FIELD_NAME_CONTRACT_ID,
  FLAT_FORM_FIELD_NAME_CONTRACT_START_DATE,
  FLAT_FORM_FIELD_NAME_CONTRACTS,
  FLAT_FORM_FIELD_NAME_ENTRANCE,
  FLAT_FORM_FIELD_NAME_ENTRANCE_ADDRESS,
  FLAT_FORM_FIELD_NAME_FILES,
  FLAT_FORM_FIELD_NAME_FLAT_NUMBER,
  FLAT_FORM_FIELD_NAME_FLOOR_NUMBER,
  FLAT_FORM_FIELD_NAME_INHABITANT,
  FLAT_FORM_FIELD_NAME_NOTES,
  FLAT_FORM_FIELD_NAME_PROPERTY_SHARE,
  FLAT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER,
  FLAT_FORM_FIELD_NAME_ROOM_TYPE,
  FLAT_FORM_FIELD_NAME_SHARE,
  FLAT_FORM_FIELD_NAME_TOTAL,
  FLAT_FORM_SHARE_FIELD_PRECISION,
  FLAT_FORM_TOTAL_FIELD_MAX_VALUE,
} from './FlatForm.constants'

export function checkFlatFormForEmptyRequired(errors) {
  const errorsList = Object.values(errors).map(value => {
    if (isArray(value)) {
      return !!value.find(
        item => item?.[FLAT_FORM_FIELD_NAME_INHABITANT] === true
      )
    }

    return value
  })

  return errorsList.includes(true)
}

export function getFlatFormContractFieldName(index, name) {
  return `${FLAT_FORM_FIELD_NAME_CONTRACTS}[${index}][${name}]`
}

export function getFlatFormValidationSchema() {
  return Yup.object().shape(
    {
      [FLAT_FORM_FIELD_NAME_BUILDING]: Yup.object().required(true),
      [FLAT_FORM_FIELD_NAME_CONTRACTS]: Yup.array().of(
        Yup.object({
          [FLAT_FORM_FIELD_NAME_CONTRACT_ID]: Yup.string(),
          [FLAT_FORM_FIELD_NAME_CONTRACT_START_DATE]: Yup.date().nullable(),
          [FLAT_FORM_FIELD_NAME_INHABITANT]: Yup.object().required(true),
          [FLAT_FORM_FIELD_NAME_PURCHASE_CONTRACT_NUMBER]: Yup.string(),
        })
      ),
      [FLAT_FORM_FIELD_NAME_ENTRANCE]: Yup.string(),
      [FLAT_FORM_FIELD_NAME_ENTRANCE_ADDRESS]: Yup.string(),
      [FLAT_FORM_FIELD_NAME_FLAT_NUMBER]: Yup.string().required(true),
      [FLAT_FORM_FIELD_NAME_FLOOR_NUMBER]: Yup.string(),
      [FLAT_FORM_FIELD_NAME_NOTES]: Yup.string(),
      [FLAT_FORM_FIELD_NAME_ROOM_TYPE]: Yup.object().nullable(),
      [FLAT_FORM_FIELD_NAME_SHARE]: Yup.number()
        .positive(i18n.t('Error.EnterPositiveNumberKey'))
        .when(FLAT_FORM_FIELD_NAME_TOTAL, {
          is: value => !!value,
          then: schema =>
            schema
              .required(true)
              .lessThan(
                Yup.ref(FLAT_FORM_FIELD_NAME_TOTAL),
                i18n.t('Error.EnterLessThanTotalKey')
              ),
        }),
      [FLAT_FORM_FIELD_NAME_TOTAL]: Yup.number()
        .positive(i18n.t('Error.EnterPositiveNumberKey'))
        .max(
          FLAT_FORM_TOTAL_FIELD_MAX_VALUE,
          i18n.t('Error.MaxValueKey', {
            value: FLAT_FORM_TOTAL_FIELD_MAX_VALUE,
          })
        )
        .when(FLAT_FORM_FIELD_NAME_SHARE, {
          is: value => !!value,
          then: schema => schema.required(true),
        }),
    },
    [FLAT_FORM_FIELD_NAME_TOTAL, FLAT_FORM_FIELD_NAME_SHARE]
  )
}

export function getFlatFormPayload(values, files) {
  let payload = getCleanValues(values)

  payload[FLAT_FORM_FIELD_NAME_BUILDING] =
    payload[FLAT_FORM_FIELD_NAME_BUILDING].value
  payload[FLAT_FORM_FIELD_NAME_FLAT_NUMBER] =
    payload[FLAT_FORM_FIELD_NAME_FLAT_NUMBER].trim()

  if (payload[FLAT_FORM_FIELD_NAME_TOTAL]) {
    payload[FLAT_FORM_FIELD_NAME_PROPERTY_SHARE] = {
      [FLAT_FORM_FIELD_NAME_SHARE]: values[FLAT_FORM_FIELD_NAME_SHARE].toFixed(
        FLAT_FORM_SHARE_FIELD_PRECISION
      ),
      [FLAT_FORM_FIELD_NAME_TOTAL]: parseInt(
        values[FLAT_FORM_FIELD_NAME_TOTAL]
      ),
    }

    payload = omit(payload, [
      FLAT_FORM_FIELD_NAME_TOTAL,
      FLAT_FORM_FIELD_NAME_SHARE,
    ])
  }

  if (payload[FLAT_FORM_FIELD_NAME_ROOM_TYPE]) {
    payload[FLAT_FORM_FIELD_NAME_ROOM_TYPE] =
      payload[FLAT_FORM_FIELD_NAME_ROOM_TYPE].value
  }

  if (payload[FLAT_FORM_FIELD_NAME_CONTRACTS]?.length > 0) {
    payload[FLAT_FORM_FIELD_NAME_CONTRACTS] = payload[
      FLAT_FORM_FIELD_NAME_CONTRACTS
    ].map(contract => {
      if (contract[FLAT_FORM_FIELD_NAME_CONTRACT_START_DATE]) {
        return {
          ...contract,
          [FLAT_FORM_FIELD_NAME_CONTRACT_START_DATE]: format(
            contract[FLAT_FORM_FIELD_NAME_CONTRACT_START_DATE],
            DATE_API_DATA_FORMAT_SHORT
          ),
          [FLAT_FORM_FIELD_NAME_INHABITANT]:
            contract[FLAT_FORM_FIELD_NAME_INHABITANT].value,
        }
      }

      return {
        ...contract,
        [FLAT_FORM_FIELD_NAME_INHABITANT]:
          contract[FLAT_FORM_FIELD_NAME_INHABITANT].value,
      }
    })
  }

  if (files.length > 0) {
    payload[FLAT_FORM_FIELD_NAME_FILES] = getItemsIds(files)
  }

  return payload
}

export function getFlatFormSuccessData(data, values) {
  return {
    address: values.building?.label?.title,
    id: data.id,
    number: data[FLAT_FORM_FIELD_NAME_FLAT_NUMBER],
  }
}

export function getFlatFormServerErrors(data) {
  const rootErrorKey = head(data.errors?.__root__)
  const errors = omit(parseServerValidation(data), '__root__')

  return rootErrorKey
    ? { ...errors, [SERVER_NON_FIELD_ERROR_KEY]: parseServerKey(rootErrorKey) }
    : errors
}

export function getFlatCreationFormSuccessNotification({
  address,
  availableSections,
  id,
  number,
}) {
  return {
    linkProps: {
      reloadDocument: !availableSections?.[ROUTE_UI_SECTION_NAME.flat],
      title: i18n.t('Notification.FlatCreatedViewFlatLinkKey'),
      to: getFlatProfilePathname({ availableSections, id }),
    },
    text: i18n.t('Notification.FlatCreatedText', {
      address,
      number,
    }),
    title: i18n.t('Notification.FlatCreatedTitle'),
    type: NOTIFICATION_TYPE.success,
  }
}

export function getFlatCreationFormErrorNotification() {
  return {
    text: i18n.t('Notification.FlatCreationErrorTextKey'),
    title: i18n.t('Notification.FlatCreationErrorTitleKey'),
  }
}
